import { ESRI, GOOGLE } from '@rsos/constants/mapProviders';
import { captureException } from '@rsos/utils/sentry';
import { ESRIMap } from '../maps/providers/esri/types';

interface BaseMapOptions {
  latitude: number;
  longitude: number;
  zoom?: number | null;
}
interface ESRIMapOptions extends BaseMapOptions {
  mapProvider: typeof ESRI;
  mapInstance?: ESRIMap;
}
interface GoogleMapOptions extends BaseMapOptions {
  mapProvider: typeof GOOGLE;
  mapInstance?: google.maps.Map;
}

/**
 * Base map options for pan and zoom.
 * @param {string} mapProvider - The map provider being used (google or esri)
 * @param {Object} mapInstance - The map instance to interact with.
 * @param {Number} latitude - The latitude coordinate to pan too.
 * @param {Number} longitude - The longitude coordinate to pan to.
 * @param {Number} zoom - Optional level to zoom the map too. If undefined
 *  will default to the zoon level for the given map provider.
 *  If null will not change the zoom level.
 */
type MapOptions = ESRIMapOptions | GoogleMapOptions;

/**
 * Pan the Esri map view state to a specific coordinate.
 * @param {Object} mapInstance - The Esri map instance object.
 * @param {Number} latitude - The latitude coordinate.
 * @param {Number} longitude - The longitude coordinate.
 * @param {Number} zoom - Zoom the map while panning. If zoom is undefined
 *  or null the map will not change zoom levels.
 */
const panEsriMapTo = (
  mapInstance: ESRIMap,
  { latitude, longitude, zoom = undefined }: BaseMapOptions,
) => {
  if (!mapInstance.view) {
    // eslint-disable-next-line no-console
    console.error('Failed to pan ESRI map. Missing view attribute.');
    return;
  }
  const view = mapInstance.view;
  const options = { animate: false };

  if (zoom === null || zoom === undefined) {
    zoom = mapInstance.view.zoom;
  }

  view
    .goTo(
      {
        center: [longitude, latitude],
        zoom,
      },
      options,
    )
    .catch(error => {
      if (error.name === 'view:goto-interrupted') {
        // Indicates that an ongoing goTo() animation or transition was
        // stopped before completion.  This should be fixed, but due to the
        // complicated map code a compromise is to log it for now.
        // This doesn't impact the applications operation.
        // eslint-disable-next-line no-console
        console.error(error);
        return;
      }
      captureException(error);
    });
};

/**
 * Pan the Google map view state to a specific coordinate.
 * @param {Object} mapInstance - The Google map instance object.
 * @param {Number} latitude - The latitude coordinate.
 * @param {Number} longitude - The longitude coordinate.
 */
const panGoogleMapTo = (
  mapInstance: google.maps.Map,
  { latitude, longitude }: BaseMapOptions,
) => {
  if (!mapInstance || !latitude || !longitude) return;

  try {
    mapInstance.panTo({
      lat: latitude,
      lng: longitude,
    });
  } catch (error) {
    captureException(error);
  }
};

/**
 * Set the Google map zoom level.
 * @param {Object} mapInstance - The Esri map instance object.
 * @param {Number} zoom - The zoom level with 10 as the default zoom level.
 */
export const setGoogleMapZoomTo = (mapInstance: google.maps.Map, zoom = 10) => {
  if (!mapInstance) {
    // eslint-disable-next-line no-console
    console.error('Failed to zoom.  Missing map instance.');
    return;
  }

  try {
    mapInstance.setZoom(zoom);
  } catch (error) {
    captureException(error);
  }
};

/**
 * Pan the map view state to a specific coordinate.
 * @param {String} mapProvider - The map provider.
 * @param {Object} mapInstance - The map instance object.
 * @param {Number} latitude - The latitude coordinate.
 * @param {Number} longitude - The longitude coordinate.
 * @param {Number} zoom - The zoom level to use for the map.
 */
export const panMapTo = (options: MapOptions) => {
  try {
    const { mapProvider, mapInstance, zoom } = options;
    let { latitude, longitude } = options;

    if (!mapProvider || !mapInstance || !latitude || !longitude) {
      // eslint-disable-next-line no-console
      console.error('Failed to pan Map. Missing required values.');
      return;
    }

    if (typeof latitude !== 'number') {
      latitude = parseFloat(latitude);
    }

    if (typeof longitude !== 'number') {
      longitude = parseFloat(longitude);
    }

    if (mapProvider === ESRI) {
      panEsriMapTo(mapInstance, {
        latitude,
        longitude,
        zoom,
      });
    } else if (mapProvider === GOOGLE) {
      panGoogleMapTo(mapInstance, {
        latitude,
        longitude,
      });

      if (zoom) {
        setGoogleMapZoomTo(mapInstance, zoom);
      }
    } else if (mapProvider satisfies never) {
      throw new Error('Unsupported map provider');
    }
  } catch (error) {
    captureException(error);
  }
};
